import React, { useState, useEffect, useCallback } from 'react';
import { Card, CardContent } from "./ui/card";
import { Button } from "./ui/button";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "./ui/tabs";
import { validateAdminCredentials, fetchDailyUpdates, fetchFileContent } from '../utils/adminUtils';
import { sendNewsletterEmail } from '../utils/emailService';
import { fetchNewsletterConfig, saveNewsletterConfig, fetchNewsletterStatus, updateNewsletterStatus } from '../utils/newsletterManagement';
import { fetchWebsiteContent, saveWebsiteContent } from '../utils/contentManagement';
import { fetchScheduleConfig, saveScheduleConfig } from '../utils/scheduleConfig';
import { fetchContactMessages, updateMessageStatus } from '../utils/contactManagement';
import { fetchSubscribers, saveSubscribers, fetchSocialMediaConfig, saveSocialMediaConfig } from '../utils/subscriberUtils';
import { getUsageStats, exportUsageData } from '../utils/userTracking';
import { FileText, Users, Share2, Settings, Mail, Send, Calendar, Clock, AlertCircle, CheckCircle, Phone, Activity, Globe, MapPin, Monitor, Briefcase, TrendingUp } from 'lucide-react';
import { useInView } from 'react-intersection-observer';
import contentCache from '../utils/contentCache';
import AdminADAJobMonitoring from './AdminADAJobMonitoring';
import JobSeekerTracker from './JobSeekerTracker';
import AnalystDailyReport from './AnalystDailyReport';


const NewsletterPreview = ({ content, selectedSubscriber, onSendTest }) => {
  if (!content) return <div>Select a daily update file to preview</div>;

  return (
    <div className="space-y-4">
      <div className="bg-white p-6 rounded-lg shadow">
        <div className="mb-6 bg-blue-600 text-white p-4 rounded-t-lg text-center">
          <h3 className="text-2xl font-bold">Nibyor Tech Newsletter</h3>
          <p className="text-sm mt-2">Your Daily Tech & Testing Updates</p>
          <p className="text-sm mt-1">{new Date().toLocaleDateString()}</p>
        </div>
        <div className="prose max-w-none space-y-8">
          {content?.aiTrends && content.aiTrends.length > 0 && (
            <section className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-xl font-bold text-blue-700 mb-4">AI Development Trends</h4>
              <div className="space-y-4">
                {content.aiTrends.map((item, i) => (
                  <div key={i} className="border-l-4 border-blue-600 pl-4">
                    <a href={item.link} className="text-lg font-semibold text-blue-600 hover:underline block mb-2">{item.title}</a>
                    <p className="text-gray-600">{item.description}</p>
                    <p className="text-sm text-gray-400 mt-1">{new Date(item.pubDate).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
          {content?.testingTrends && content.testingTrends.length > 0 && (
            <section className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-xl font-bold text-purple-700 mb-4">Testing News</h4>
              <div className="space-y-4">
                {content.testingTrends.map((item, i) => (
                  <div key={i} className="border-l-4 border-purple-600 pl-4">
                    <a href={item.link} className="text-lg font-semibold text-purple-600 hover:underline block mb-2">{item.title}</a>
                    <p className="text-gray-600">{item.description}</p>
                    <p className="text-sm text-gray-400 mt-1">{new Date(item.pubDate).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
          {content?.industryTrends && content.industryTrends.length > 0 && (
            <section className="bg-gray-50 p-4 rounded-lg">
              <h4 className="text-xl font-bold text-green-700 mb-4">Industry Updates</h4>
              <div className="space-y-4">
                {content.industryTrends.map((item, i) => (
                  <div key={i} className="border-l-4 border-green-600 pl-4">
                    <a href={item.link} className="text-lg font-semibold text-green-600 hover:underline block mb-2">{item.title}</a>
                    <p className="text-gray-600">{item.description}</p>
                    <p className="text-sm text-gray-400 mt-1">{new Date(item.pubDate).toLocaleDateString()}</p>
                  </div>
                ))}
              </div>
            </section>
          )}
        </div>
        <div className="mt-8 text-center text-sm text-gray-500 pt-4 border-t">
          <p>© {new Date().getFullYear()} Nibyor Tech. All rights reserved.</p>
          <p>You received this email because you subscribed to our newsletter.</p>
        </div>
      </div>
      <div className="flex items-center gap-4">
        <Button
          onClick={() => onSendTest(selectedSubscriber)}
          className="bg-blue-600 text-white flex items-center gap-2"
          disabled={!selectedSubscriber || !content}
        >
          <Send className="w-4 h-4" />
          Send Test Email
        </Button>
        <p className="text-sm text-gray-500">
          {selectedSubscriber ? `Will be sent to: ${selectedSubscriber}` : 'Select a subscriber to send test'}
        </p>
      </div>
    </div>
  );
};

const TrackingDashboard = () => {
  const [trackingData, setTrackingData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [stats, setStats] = useState({});
  const [filters, setFilters] = useState({
    startDate: '',
    endDate: '',
    country: '',
    url: ''
  });
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;

  useEffect(() => {
    loadTrackingData();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [trackingData, filters]);

  const loadTrackingData = () => {
    try {
      const data = exportUsageData();
      const statistics = getUsageStats();
      setTrackingData(data);
      setStats(statistics);
    } catch (error) {
      console.error('Error loading tracking data:', error);
    }
  };

  const applyFilters = () => {
    let filtered = exportUsageData(filters);
    setFilteredData(filtered);
    setCurrentPage(1);
  };

  const handleFilterChange = (field, value) => {
    setFilters(prev => ({
      ...prev,
      [field]: value
    }));
  };

  const exportToCSV = () => {
    const csvContent = "data:text/csv;charset=utf-8," 
      + "Timestamp,URL,Test Type,Duration,Issues Found,Success,Country,Region,City,IP,Browser,OS,Session ID\n"
      + filteredData.map(row => 
        `"${row.timestamp}","${row.url}","${row.testType}","${row.duration || 'N/A'}","${row.issues_found}","${row.success}","${row.location?.country || 'Unknown'}","${row.location?.region || 'Unknown'}","${row.location?.city || 'Unknown'}","${row.location?.ip || 'Unknown'}","${row.browser?.browser || 'Unknown'}","${row.browser?.os || 'Unknown'}","${row.session_id}"`
      ).join("\n");
    
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement("a");
    link.setAttribute("href", encodedUri);
    link.setAttribute("download", `ada_usage_tracking_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const paginatedData = filteredData.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage
  );

  const totalPages = Math.ceil(filteredData.length / itemsPerPage);

  return (
    <div className="space-y-6">
      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Globe className="w-8 h-8 text-blue-500 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-600">Total Tests</p>
                <p className="text-2xl font-bold">{stats.totalTests || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <CheckCircle className="w-8 h-8 text-green-500 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-600">Success Rate</p>
                <p className="text-2xl font-bold">{stats.successRate || 0}%</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Users className="w-8 h-8 text-purple-500 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-600">Unique Users</p>
                <p className="text-2xl font-bold">{stats.uniqueUsers || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>

        <Card>
          <CardContent className="p-4">
            <div className="flex items-center">
              <Activity className="w-8 h-8 text-orange-500 mr-3" />
              <div>
                <p className="text-sm font-medium text-gray-600">Recent Activity (24h)</p>
                <p className="text-2xl font-bold">{stats.recentActivity || 0}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Filters */}
      <Card>
        <CardContent className="p-4">
          <h3 className="text-lg font-semibold mb-4">Filters</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Start Date</label>
              <input
                type="date"
                value={filters.startDate}
                onChange={(e) => handleFilterChange('startDate', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">End Date</label>
              <input
                type="date"
                value={filters.endDate}
                onChange={(e) => handleFilterChange('endDate', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Country</label>
              <input
                type="text"
                placeholder="Enter country name"
                value={filters.country}
                onChange={(e) => handleFilterChange('country', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">URL</label>
              <input
                type="text"
                placeholder="Enter URL to filter"
                value={filters.url}
                onChange={(e) => handleFilterChange('url', e.target.value)}
                className="w-full p-2 border border-gray-300 rounded-md"
              />
            </div>
          </div>
          <div className="flex gap-2 mt-4">
            <Button onClick={() => setFilters({ startDate: '', endDate: '', country: '', url: '' })}>
              Clear Filters
            </Button>
            <Button onClick={exportToCSV} className="bg-green-600 hover:bg-green-700">
              Export to CSV
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Country Statistics */}
      {stats.countryStats && Object.keys(stats.countryStats).length > 0 && (
        <Card>
          <CardContent className="p-4">
            <h3 className="text-lg font-semibold mb-4">Usage by Country</h3>
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4">
              {Object.entries(stats.countryStats)
                .sort(([,a], [,b]) => b - a)
                .slice(0, 12)
                .map(([country, count]) => (
                  <div key={country} className="flex items-center justify-between p-2 bg-gray-50 rounded">
                    <span className="text-sm font-medium">{country}</span>
                    <span className="text-sm text-gray-600">{count}</span>
                  </div>
                ))}
            </div>
          </CardContent>
        </Card>
      )}

      {/* Tracking Data Table */}
      <Card>
        <CardContent className="p-4">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold">Usage History ({filteredData.length} records)</h3>
            <Button onClick={loadTrackingData} size="sm">
              Refresh Data
            </Button>
          </div>
          
          <div className="overflow-x-auto">
            <table className="min-w-full table-auto">
              <thead>
                <tr className="bg-gray-50">
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Timestamp</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">URL</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Location</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">IP</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Browser</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Issues</th>
                  <th className="px-4 py-2 text-left text-sm font-medium text-gray-700">Status</th>
                </tr>
              </thead>
              <tbody>
                {paginatedData.map((record, index) => (
                  <tr key={record.id} className={index % 2 === 0 ? 'bg-white' : 'bg-gray-50'}>
                    <td className="px-4 py-2 text-sm">
                      {new Date(record.timestamp).toLocaleString()}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <div className="max-w-xs truncate" title={record.url}>
                        {record.url}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <div className="flex items-center">
                        <MapPin className="w-3 h-3 mr-1" />
                        {record.location?.city}, {record.location?.country}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-sm font-mono">
                      {record.location?.ip || 'Unknown'}
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <div className="flex items-center">
                        <Monitor className="w-3 h-3 mr-1" />
                        {record.browser?.browser} / {record.browser?.os}
                      </div>
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <span className={`px-2 py-1 rounded text-xs ${record.issues_found > 0 ? 'bg-red-100 text-red-800' : 'bg-green-100 text-green-800'}`}>
                        {record.issues_found}
                      </span>
                    </td>
                    <td className="px-4 py-2 text-sm">
                      <span className={`px-2 py-1 rounded text-xs ${record.success ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'}`}>
                        {record.success ? 'Success' : 'Failed'}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="flex justify-between items-center mt-4">
              <span className="text-sm text-gray-700">
                Showing {(currentPage - 1) * itemsPerPage + 1} to {Math.min(currentPage * itemsPerPage, filteredData.length)} of {filteredData.length} records
              </span>
              <div className="flex gap-2">
                <Button
                  onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))}
                  disabled={currentPage === 1}
                  size="sm"
                >
                  Previous
                </Button>
                <span className="flex items-center px-3 py-1 text-sm">
                  Page {currentPage} of {totalPages}
                </span>
                <Button
                  onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))}
                  disabled={currentPage === totalPages}
                  size="sm"
                >
                  Next
                </Button>
              </div>
            </div>
          )}

          {filteredData.length === 0 && (
            <div className="text-center py-8">
              <p className="text-gray-500">No tracking data available. Start using the ADA checker to see usage analytics here.</p>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const AdminPanel = ({ onAuthenticationChange, isAuthenticated: externalIsAuthenticated = false, showBackground = true }) => {
  const [isAuthenticated, setIsAuthenticated] = useState(externalIsAuthenticated);
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [error, setError] = useState('');
  const [activeTab, setActiveTab] = useState('updates');
  const [loading, setLoading] = useState(false);
  // Debounced flag to avoid flashing spinners for very short loads
  const [showLoadingIndicator, setShowLoadingIndicator] = useState(false);
  const [previewLoading, setPreviewLoading] = useState(false);
  const loadingTimerRef = React.useRef(null);

  // Manage debounced loading indicator
  useEffect(() => {
    if (loading) {
      // delay before showing indicator
      loadingTimerRef.current = setTimeout(() => setShowLoadingIndicator(true), 200);
    } else {
      clearTimeout(loadingTimerRef.current);
      setShowLoadingIndicator(false);
    }
    return () => clearTimeout(loadingTimerRef.current);
  }, [loading]);

  // Daily Updates State
  const [dailyUpdates, setDailyUpdates] = useState([]);
  const [selectedFile, setSelectedFile] = useState(null);
  const [fileContent, setFileContent] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [hasMore, setHasMore] = useState(true);

  // Subscribers State
  const [subscribers, setSubscribers] = useState([]);
  const [subscribersSha, setSubscribersSha] = useState(null);
  const [newSubscriber, setNewSubscriber] = useState('');

  // Social Media State
  const [socialConfig, setSocialConfig] = useState({});
  const [socialConfigSha, setSocialConfigSha] = useState(null);

  // Test Newsletter State
  const [selectedTestSubscriber, setSelectedTestSubscriber] = useState('');
  const [testStatus, setTestStatus] = useState('');

  // Newsletter Management State
  const [newsletterConfig, setNewsletterConfig] = useState({
    scheduleTime: '09:00',
    frequency: 'daily',
    lastRun: null
  });
  const [websiteContent, setWebsiteContent] = useState({
    aiTrends: [],
    testingTrends: [],
    industryTrends: [],
    lastUpdated: null
  });
  const [websiteContentSha, setWebsiteContentSha] = useState(null);
  const [editingContent, setEditingContent] = useState(null);
  const [configSha, setConfigSha] = useState(null);
  const [newsletterStatus, setNewsletterStatus] = useState([]);
  const [statusSha, setStatusSha] = useState(null);

  // Schedule Management State
  const [scheduleConfig, setScheduleConfig] = useState({
    time: '17:00',
    timezone: 'Asia/Kolkata',
    days: ['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday'],
    enabled: true
  });
  const [scheduleConfigSha, setScheduleConfigSha] = useState(null);

  // Contact Management State
  const [contactMessages, setContactMessages] = useState([]);
  const [contactMessagesSha, setContactMessagesSha] = useState(null);

  // Virtualized Daily Updates State
  const [virtualizedItems, setVirtualizedItems] = useState([]);
  const [startIndex, setStartIndex] = useState(0);
  const itemsPerPage = 20;
  const { ref, inView } = useInView();

  useEffect(() => {
    if (isAuthenticated) {
      loadAllData();
    }
  }, [isAuthenticated]);

  // Sync external authentication state
  useEffect(() => {
    setIsAuthenticated(externalIsAuthenticated);
  }, [externalIsAuthenticated]);

  const loadAllData = async () => {
    setLoading(true);
    try {
      const [updates, subsData, configData, statusData, contentData, scheduleData, contactData] = await Promise.all([
        fetchDailyUpdates(),
        fetchSubscribers(),
        fetchNewsletterConfig(),
        fetchNewsletterStatus(),
        fetchWebsiteContent(),
        fetchScheduleConfig(),
        fetchContactMessages()
      ]);

      setDailyUpdates(updates.sort((a, b) => b.name.localeCompare(a.name)));
      setSubscribers(subsData.subscribers);
      setSubscribersSha(subsData.sha);
      setNewsletterConfig(configData.config);
      setConfigSha(configData.sha);
      setNewsletterStatus(statusData.status);
      setStatusSha(statusData.sha);
      setWebsiteContent(contentData.content);
      setWebsiteContentSha(contentData.sha);
      setScheduleConfig(scheduleData.config);
      setScheduleConfigSha(scheduleData.sha);
      setContactMessages(contactData.messages);
      setContactMessagesSha(contactData.sha);
    } catch (error) {
      setError('Failed to load data');
    } finally {
      setLoading(false);
    }
  };

  const loadInitialData = useCallback(async () => {
    if (!isAuthenticated) return;
    
    setLoading(true);
    try {
      const [updates, subsData] = await Promise.all([
        fetchDailyUpdates(),
        fetchSubscribers()
      ]);

      setDailyUpdates(updates || []);
      setSubscribers(subsData?.subscribers || []);
    } catch (err) {
      setError('Failed to load data');
      console.error('Load error:', err);
    } finally {
      setLoading(false);
    }
  }, [isAuthenticated]);

  useEffect(() => {
    loadInitialData();
  }, [loadInitialData]);

  const handleLogin = (e) => {
    e.preventDefault();
    if (validateAdminCredentials(username, password)) {
      setIsAuthenticated(true);
      setError('');
      // Notify parent component about authentication change
      if (onAuthenticationChange) {
        onAuthenticationChange(true);
      }
    } else {
      setError('Invalid credentials');
    }
  };

  const loadMoreItems = useCallback(async () => {
    if (loading) return;
    try {
      setLoading(true);
      const updates = await fetchDailyUpdates();
      setDailyUpdates(prev => {
        // Filter out duplicates
        const newUpdates = updates.filter(
          update => !prev.some(p => p.sha === update.sha)
        );
        return [...prev, ...newUpdates];
      });
      setHasMore(updates.length > 0);
    } finally {
      setLoading(false);
    }
  }, [loading]);

  useEffect(() => {
    if (inView && hasMore) {
      loadMoreItems();
    }
  }, [inView, hasMore, loadMoreItems]);

  const viewFileContent = useCallback(async (file) => {
    if (!file) return;
    
    try {
      setSelectedFile(file);
      
      // Check cache first
      if (contentCache.hasContent(file.path)) {
        setFileContent(contentCache.getContent(file.path));
        return;
      }

      setLoading(true);
      const content = await fetchFileContent(file.path);
      
      if (content) {
        // Store in cache
        contentCache.setContent(file.path, content);
        setFileContent(content);

        // Preload next file in background
        const currentIndex = dailyUpdates.findIndex(f => f.path === file.path);
        if (currentIndex > -1 && currentIndex < dailyUpdates.length - 1) {
          const nextFile = dailyUpdates[currentIndex + 1];
          fetchFileContent(nextFile.path)
            .then(nextContent => contentCache.setContent(nextFile.path, nextContent))
            .catch(() => {});
        }
      }
    } catch (error) {
      console.error('Preview error:', error);
      setError('Failed to load preview');
    } finally {
      setLoading(false);
    }
  }, [dailyUpdates]);

  const handleAddSubscriber = async (e) => {
    e.preventDefault();
    if (!newSubscriber || !newSubscriber.includes('@')) {
      setError('Please enter a valid email');
      return;
    }

    try {
      setLoading(true);
      const updatedSubscribers = [...subscribers, { 
        email: newSubscriber, 
        dateAdded: new Date().toISOString(),
        active: true 
      }];
      await saveSubscribers(updatedSubscribers, subscribersSha);
      setSubscribers(updatedSubscribers);
      setNewSubscriber('');
      setError('');
    } catch (error) {
      setError('Failed to add subscriber');
    } finally {
      setLoading(false);
    }
  };

  const handleRemoveSubscriber = async (email) => {
    try {
      setLoading(true);
      const updatedSubscribers = subscribers.filter(s => s.email !== email);
      await saveSubscribers(updatedSubscribers, subscribersSha);
      setSubscribers(updatedSubscribers);
    } catch (error) {
      setError('Failed to remove subscriber');
    } finally {
      setLoading(false);
    }
  };

  const handleSocialConfigUpdate = async (platform, config) => {
    try {
      setLoading(true);
      const updatedConfig = {
        ...socialConfig,
        [platform]: { ...socialConfig[platform], ...config }
      };
      await saveSocialMediaConfig(updatedConfig, socialConfigSha);
      setSocialConfig(updatedConfig);
      setError('');
    } catch (error) {
      setError('Failed to update social media configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleSendTestNewsletter = async (email) => {
    if (!fileContent) {
      setTestStatus('Please select a newsletter content first');
      return;
    }
    
    try {
      setLoading(true);
      setTestStatus('Sending test newsletter...');
      
      await sendNewsletterEmail(email, fileContent);
      
      setTestStatus(`Test newsletter sent successfully to ${email}`);
    } catch (error) {
      setTestStatus('Failed to send test newsletter: ' + error.message);
      console.error('Test newsletter error:', error);
    } finally {
      setLoading(false);
    }
  };

  const handleConfigUpdate = async (updates) => {
    try {
      setLoading(true);
      const newConfig = { ...newsletterConfig, ...updates };
      await saveNewsletterConfig(newConfig, configSha);
      setNewsletterConfig(newConfig);
      setError('');
    } catch (error) {
      setError('Failed to update newsletter configuration');
    } finally {
      setLoading(false);
    }
  };

  const generateNewsletter = async () => {
    try {
      setLoading(true);
      
      // Check for duplicate
      const today = new Date().toISOString().split('T')[0];
      const alreadyGenerated = newsletterStatus.some(
        status => status.date.startsWith(today)
      );
      
      if (alreadyGenerated) {
        setError('Newsletter already generated for today');
        return;
      }

      // Generate newsletter
      const newStatus = {
        date: new Date().toISOString(),
        recipientCount: subscribers.length,
        status: 'success'
      };

      const updatedStatus = [newStatus, ...newsletterStatus];
      await updateNewsletterStatus(updatedStatus, statusSha);
      
      setNewsletterStatus(updatedStatus);
      setError('');
    } catch (error) {
      setError('Failed to generate newsletter');
    } finally {
      setLoading(false);
    }
  };

  const handleContentEdit = async (section, content) => {
    try {
      const updatedContent = { ...websiteContent };
      updatedContent[section] = content;
      updatedContent.lastUpdated = new Date().toISOString();
      
      await saveWebsiteContent(updatedContent, websiteContentSha);
      setWebsiteContent(updatedContent);
      setEditingContent(null);
      setError('');
    } catch (error) {
      setError('Failed to update content');
    }
  };

  const handleContentPublish = async () => {
    try {
      setLoading(true);
      await saveWebsiteContent({
        ...websiteContent,
        lastUpdated: new Date().toISOString()
      }, websiteContentSha);
      setError('');
    } catch (error) {
      setError('Failed to publish content');
    } finally {
      setLoading(false);
    }
  };

  const getNextGenerationTime = () => {
    if (!scheduleConfig?.enabled || !scheduleConfig?.time || !scheduleConfig?.days?.length) {
      return 'Not scheduled';
    }

    const now = new Date();
    const [hours, minutes] = scheduleConfig.time.split(':').map(Number);
    
    // Find the next scheduled day
    let nextDate = new Date(now);
    nextDate.setHours(hours, minutes, 0, 0);
    
    if (nextDate <= now) {
      nextDate.setDate(nextDate.getDate() + 1);
    }

    while (!scheduleConfig.days.includes(
      nextDate.toLocaleDateString('en-US', { weekday: 'long' })
    )) {
      nextDate.setDate(nextDate.getDate() + 1);
    }

    return nextDate.toLocaleString('en-IN', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
      hour12: true,
      timeZone: 'Asia/Kolkata'
    });
  };

  const handleScheduleUpdate = async (updates) => {
    try {
      setLoading(true);
      const newConfig = { ...scheduleConfig, ...updates };
      await saveScheduleConfig(newConfig, scheduleConfigSha);
      setScheduleConfig(newConfig);
      setError('');
    } catch (error) {
      setError('Failed to update schedule configuration');
    } finally {
      setLoading(false);
    }
  };

  const handleMessageStatus = async (messageId, status) => {
    try {
      setLoading(true);
      const updatedMessages = await updateMessageStatus(messageId, status, contactMessages, contactMessagesSha);
      setContactMessages(updatedMessages);
      setError('');
    } catch (error) {
      setError('Failed to update message status');
    } finally {
      setLoading(false);
    }
  };

  const loadDailyUpdates = useCallback(async (page) => {
    try {
      setLoading(true);
      const { files, hasMore: more } = await fetchDailyUpdates(page);
      setDailyUpdates(prev => page === 1 ? files : [...prev, ...files]);
      setHasMore(more);
      
      // Prefetch content for the first few files
      await prefetchFileContent(files);
    } catch (error) {
      setError('Failed to load updates');
    } finally {
      setLoading(false);
    }
  }, []);

  const handleLoadMore = () => {
    if (!loading && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      loadDailyUpdates(nextPage);
    }
  };

  const handleLogout = () => {
    setIsAuthenticated(false);
    setUsername('');
    setPassword('');
    setError('');
    // Notify parent component about authentication change
    if (onAuthenticationChange) {
      onAuthenticationChange(false);
    }
  };

  if (!isAuthenticated) {
    const loginFormContent = (
      <Card className="w-full max-w-sm bg-white rounded-xl shadow-xl border-0">
        <CardContent className="p-6">
          <div className="text-center mb-6">
            <h2 className="text-2xl font-bold text-gray-900">Admin Login</h2>
            <p className="text-gray-600 text-sm mt-1">Access your dashboard</p>
          </div>
          
          <form onSubmit={handleLogin} className="space-y-4">
            <div>
              <input
                type="text"
                value={username}
                onChange={(e) => setUsername(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                placeholder="Username"
                required
              />
            </div>
            <div>
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="w-full p-3 border border-gray-300 rounded-lg text-sm focus:ring-2 focus:ring-blue-500 focus:border-blue-500 transition-all"
                placeholder="Password"
                required
              />
            </div>
            {error && (
              <div className="bg-red-50 border border-red-200 rounded-lg p-3 text-red-700 text-sm">
                {error}
              </div>
            )}
            <Button 
              type="submit" 
              className="w-full bg-blue-600 hover:bg-blue-700 text-white py-3 rounded-lg text-sm font-medium transition-all shadow-md hover:shadow-lg"
            >
              Sign In
            </Button>
          </form>
          
          <div className="mt-4 text-center">
            <p className="text-xs text-gray-500">Secure admin access</p>
          </div>
        </CardContent>
      </Card>
    );

    if (showBackground) {
      return (
        <div className="min-h-screen bg-gradient-to-br from-slate-900 via-gray-900 to-slate-900">
          {/* Login Form positioned directly under existing header */}
          <div className="flex justify-center px-4 pt-4">
            {loginFormContent}
          </div>
        </div>
      );
    } else {
      return loginFormContent;
    }
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-gray-50 to-blue-50">
      <div className="max-w-7xl mx-auto p-3">
        
        {/* Compact Header */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 p-4 mb-4">
          <div className="flex justify-between items-center">
            <div className="flex items-center">
              <div className="inline-flex items-center justify-center w-10 h-10 bg-gradient-to-r from-orange-500 to-red-600 rounded-lg mr-3">
                <Activity className="w-5 h-5 text-white" />
              </div>
              <div>
                <h1 className="text-2xl font-bold text-gray-900">Admin Dashboard</h1>
                <p className="text-sm text-gray-600">Manage your Nibyor platform</p>
              </div>
            </div>
            <Button
              onClick={handleLogout}
              className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg text-sm font-medium transition-all"
            >
              Sign Out
            </Button>
          </div>
        </div>

        {/* Compact Stats Overview */}
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
            <div className="flex items-center">
              <div className="p-2 bg-blue-100 rounded-md">
                <Users className="w-4 h-4 text-blue-600" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Subscribers</p>
                <p className="text-lg font-bold text-gray-900">{subscribers.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
            <div className="flex items-center">
              <div className="p-2 bg-green-100 rounded-md">
                <Mail className="w-4 h-4 text-green-600" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Daily Updates</p>
                <p className="text-lg font-bold text-gray-900">{dailyUpdates.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
            <div className="flex items-center">
              <div className="p-2 bg-purple-100 rounded-md">
                <Phone className="w-4 h-4 text-purple-600" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">Messages</p>
                <p className="text-lg font-bold text-gray-900">{contactMessages.length}</p>
              </div>
            </div>
          </div>
          <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-100">
            <div className="flex items-center">
              <div className="p-2 bg-orange-100 rounded-md">
                <Monitor className="w-4 h-4 text-orange-600" />
              </div>
              <div className="ml-3">
                <p className="text-xs font-medium text-gray-600">System Status</p>
                <p className="text-lg font-bold text-green-600">Online</p>
              </div>
            </div>
          </div>
        </div>

        {/* Compact Main Content Tabs */}
        <div className="bg-white rounded-xl shadow-md border border-gray-200 overflow-hidden">
          <Tabs defaultValue={activeTab} onValueChange={setActiveTab} className="w-full">
            <TabsList className="w-full bg-gray-50 p-1 overflow-x-auto flex flex-nowrap gap-1 justify-start">
              <TabsTrigger 
                value="updates" 
                className="flex items-center gap-1 px-2 sm:px-3 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all text-xs sm:text-sm whitespace-nowrap"
              >
                <FileText className="w-4 h-4" />
                <span className="hidden sm:inline">Updates</span>
              </TabsTrigger>
              <TabsTrigger 
                value="subscribers" 
                className="flex items-center gap-1 px-2 sm:px-3 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all text-xs sm:text-sm whitespace-nowrap"
              >
                <Users className="w-4 h-4" />
                <span className="hidden sm:inline">Subscribers</span>
              </TabsTrigger>
              <TabsTrigger 
                value="test" 
                className="flex items-center gap-1 px-2 sm:px-3 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all text-xs sm:text-sm whitespace-nowrap"
              >
                <Mail className="w-4 h-4" />
                <span className="hidden sm:inline">Test Mail</span>
              </TabsTrigger>
              <TabsTrigger 
                value="manage" 
                className="flex items-center gap-1 px-2 sm:px-3 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all text-xs sm:text-sm whitespace-nowrap"
              >
                <Settings className="w-4 h-4" />
                <span className="hidden sm:inline">Manage</span>
              </TabsTrigger>
              <TabsTrigger 
                value="social" 
                className="flex items-center gap-1 px-2 sm:px-3 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all text-xs sm:text-sm whitespace-nowrap"
              >
                <Share2 className="w-4 h-4" />
                <span className="hidden sm:inline">Social</span>
              </TabsTrigger>
              <TabsTrigger 
                value="contacts" 
                className="flex items-center gap-1 px-2 sm:px-3 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all text-xs sm:text-sm whitespace-nowrap"
              >
                <Phone className="w-4 h-4" />
                <span className="hidden sm:inline">Contacts</span>
              </TabsTrigger>
              <TabsTrigger 
                value="tracking" 
                className="flex items-center gap-1 px-2 sm:px-3 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all text-xs sm:text-sm whitespace-nowrap"
              >
                <Activity className="w-4 h-4" />
                <span className="hidden sm:inline">Tracking</span>
              </TabsTrigger>
              <TabsTrigger 
                value="jobs" 
                className="flex items-center gap-1 px-2 sm:px-3 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all text-xs sm:text-sm whitespace-nowrap"
              >
                <Briefcase className="w-4 h-4" />
                <span className="hidden sm:inline">ADA Jobs</span>
              </TabsTrigger>
              <TabsTrigger 
                value="analyst" 
                className="flex items-center gap-1 px-2 sm:px-3 py-2 rounded-md data-[state=active]:bg-white data-[state=active]:shadow-sm transition-all text-xs sm:text-sm whitespace-nowrap"
              >
                <TrendingUp className="w-4 h-4" />
                <span className="hidden sm:inline">Analyst</span>
              </TabsTrigger>

            </TabsList>

            {/* Compact Tab Content */}
            <div className="p-4">
              <TabsContent value="updates" className="mt-0">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                  <div className="lg:col-span-4">
                    <Card className="shadow-sm border border-gray-200">
                      <CardContent className="p-4">
                        <h3 className="text-lg font-semibold mb-3 text-gray-900">Daily Updates</h3>
                        <div className="space-y-1 max-h-[300px] lg:max-h-[450px] overflow-auto">
                          {dailyUpdates.map((file) => (
                            <Button
                              key={file.sha}
                              onClick={() => viewFileContent(file)}
                              className={`w-full justify-start text-xs sm:text-sm py-2 ${
                                selectedFile?.sha === file.sha
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                              }`}
                            >
                              {file.name}
                            </Button>
                          ))}
                          {hasMore && <div ref={ref} className="h-8" />}
                          {showLoadingIndicator && (
                            <div className="flex justify-center py-2">
                              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                            </div>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="lg:col-span-8">
                    <Card>
                      <CardContent className="p-4">
                        <h3 className="text-lg font-semibold mb-3">
                          {selectedFile ? selectedFile.name : 'Select a file to view'}
                        </h3>
                        {previewLoading ? (
                          <div className="flex justify-center py-6">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600" />
                          </div>
                        ) : fileContent ? (
                          <div className="bg-gray-800 text-white p-3 rounded-lg overflow-auto max-h-[300px] lg:max-h-[450px]">
                            <pre className="whitespace-pre-wrap text-xs">
                              {JSON.stringify(fileContent, null, 2)}
                            </pre>
                          </div>
                        ) : (
                          <p className="text-gray-500 text-center py-6">
                            {selectedFile ? 'Loading preview...' : 'No file selected'}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="subscribers">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                  <div className="lg:col-span-8">
                    <Card>
                      <CardContent className="p-4">
                        <h3 className="text-lg font-semibold mb-3">Subscriber List</h3>
                        <div className="space-y-2 max-h-[400px] lg:max-h-[500px] overflow-auto">
                          {subscribers.map((subscriber) => (
                            <div key={subscriber.email} className="flex flex-col sm:flex-row justify-between sm:items-center p-2 bg-gray-50 rounded-md gap-2">
                              <div className="flex-1">
                                <p className="font-medium text-sm">{subscriber.email}</p>
                                <p className="text-xs text-gray-500">Added: {new Date(subscriber.dateAdded).toLocaleDateString()}</p>
                              </div>
                              <Button
                                onClick={() => handleRemoveSubscriber(subscriber.email)}
                                variant="destructive"
                                size="sm"
                                className="text-xs px-2 py-1 w-full sm:w-auto"
                              >
                                Remove
                              </Button>
                            </div>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="lg:col-span-4">
                    <Card>
                      <CardContent className="p-4">
                        <h3 className="text-lg font-semibold mb-3">Add Subscriber</h3>
                        <form onSubmit={handleAddSubscriber} className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium mb-1">Email</label>
                            <input
                              type="email"
                              value={newSubscriber}
                              onChange={(e) => setNewSubscriber(e.target.value)}
                              className="w-full p-2 border rounded text-sm"
                              placeholder="subscriber@example.com"
                              required
                            />
                          </div>
                          <Button type="submit" className="w-full text-sm">Add Subscriber</Button>
                        </form>
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="test">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                  <div className="lg:col-span-4">
                    <Card>
                      <CardContent className="p-4">
                        <h3 className="text-lg font-semibold mb-3">Select Test Subscriber</h3>
                        <div className="space-y-1 max-h-[300px] lg:max-h-[500px] overflow-auto">
                          {subscribers.map((subscriber) => (
                            <Button
                              key={subscriber.email}
                              onClick={() => setSelectedTestSubscriber(subscriber.email)}
                              className={`w-full justify-start text-xs sm:text-sm py-2 ${
                                selectedTestSubscriber === subscriber.email
                                  ? 'bg-blue-600 text-white'
                                  : 'bg-gray-100 text-gray-800 hover:bg-gray-200'
                              }`}
                            >
                              {subscriber.email}
                            </Button>
                          ))}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="lg:col-span-8">
                    <NewsletterPreview
                      content={fileContent}
                      selectedSubscriber={selectedTestSubscriber}
                      onSendTest={handleSendTestNewsletter}
                    />
                    {testStatus && (
                      <p className={`mt-3 p-3 rounded text-sm ${
                        testStatus.includes('success')
                          ? 'bg-green-100 text-green-700'
                          : 'bg-blue-100 text-blue-700'
                      }`}>
                        {testStatus}
                      </p>
                    )}
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="social">
                <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
                  <Card>
                    <CardContent className="p-4">
                      <h3 className="text-lg font-semibold mb-3">Facebook</h3>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium mb-1">Page ID</label>
                          <input
                            type="text"
                            value={socialConfig?.facebook?.pageId || ''}
                            onChange={(e) => handleSocialConfigUpdate('facebook', { pageId: e.target.value })}
                            className="w-full p-2 border rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Access Token</label>
                          <input
                            type="password"
                            value={socialConfig?.facebook?.accessToken || ''}
                            onChange={(e) => handleSocialConfigUpdate('facebook', { accessToken: e.target.value })}
                            className="w-full p-2 border rounded text-sm"
                          />
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={socialConfig?.facebook?.enabled || false}
                            onChange={(e) => handleSocialConfigUpdate('facebook', { enabled: e.target.checked })}
                            className="mr-2"
                          />
                          <label className="text-sm">Enable Facebook Sharing</label>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <h3 className="text-lg font-semibold mb-3">Twitter</h3>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium mb-1">API Key</label>
                          <input
                            type="password"
                            value={socialConfig?.twitter?.apiKey || ''}
                            onChange={(e) => handleSocialConfigUpdate('twitter', { apiKey: e.target.value })}
                            className="w-full p-2 border rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">API Secret</label>
                          <input
                            type="password"
                            value={socialConfig?.twitter?.apiSecret || ''}
                            onChange={(e) => handleSocialConfigUpdate('twitter', { apiSecret: e.target.value })}
                            className="w-full p-2 border rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Access Token</label>
                          <input
                            type="password"
                            value={socialConfig?.twitter?.accessToken || ''}
                            onChange={(e) => handleSocialConfigUpdate('twitter', { accessToken: e.target.value })}
                            className="w-full p-2 border rounded text-sm"
                          />
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={socialConfig?.twitter?.enabled || false}
                            onChange={(e) => handleSocialConfigUpdate('twitter', { enabled: e.target.checked })}
                            className="mr-2"
                          />
                          <label className="text-sm">Enable Twitter Sharing</label>
                        </div>
                      </div>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardContent className="p-4">
                      <h3 className="text-lg font-semibold mb-3">LinkedIn</h3>
                      <div className="space-y-3">
                        <div>
                          <label className="block text-sm font-medium mb-1">Client ID</label>
                          <input
                            type="text"
                            value={socialConfig?.linkedin?.clientId || ''}
                            onChange={(e) => handleSocialConfigUpdate('linkedin', { clientId: e.target.value })}
                            className="w-full p-2 border rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Client Secret</label>
                          <input
                            type="password"
                            value={socialConfig?.linkedin?.clientSecret || ''}
                            onChange={(e) => handleSocialConfigUpdate('linkedin', { clientSecret: e.target.value })}
                            className="w-full p-2 border rounded text-sm"
                          />
                        </div>
                        <div>
                          <label className="block text-sm font-medium mb-1">Access Token</label>
                          <input
                            type="password"
                            value={socialConfig?.linkedin?.accessToken || ''}
                            onChange={(e) => handleSocialConfigUpdate('linkedin', { accessToken: e.target.value })}
                            className="w-full p-2 border rounded text-sm"
                          />
                        </div>
                        <div className="flex items-center">
                          <input
                            type="checkbox"
                            checked={socialConfig?.linkedin?.enabled || false}
                            onChange={(e) => handleSocialConfigUpdate('linkedin', { enabled: e.target.checked })}
                            className="mr-2"
                          />
                          <label className="text-sm">Enable LinkedIn Sharing</label>
                        </div>
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="manage">
                <div className="grid grid-cols-1 lg:grid-cols-12 gap-4">
                  <div className="lg:col-span-4">
                    <Card>
                      <CardContent className="p-4">
                        <h3 className="text-lg font-semibold mb-3">Newsletter Schedule</h3>
                        <div className="space-y-3">
                          <div>
                            <label className="block text-sm font-medium mb-1">Schedule Time (IST)</label>
                            <input
                              type="time"
                              value={scheduleConfig?.time || '17:00'}
                              onChange={(e) => handleScheduleUpdate({ time: e.target.value })}
                              className="w-full p-2 border rounded text-sm"
                            />
                          </div>
                          <div>
                            <label className="block text-sm font-medium mb-1">Schedule Days</label>
                            <div className="grid grid-cols-2 gap-1">
                              {['Monday', 'Tuesday', 'Wednesday', 'Thursday', 'Friday', 'Saturday', 'Sunday'].map(day => (
                                <label key={day} className="flex items-center space-x-1">
                                  <input
                                    type="checkbox"
                                    checked={scheduleConfig?.days?.includes(day)}
                                    onChange={(e) => {
                                      const days = e.target.checked
                                        ? [...(scheduleConfig?.days || []), day]
                                        : (scheduleConfig?.days || []).filter(d => d !== day);
                                      handleScheduleUpdate({ days });
                                    }}
                                    className="rounded"
                                  />
                                  <span className="text-xs">{day.substring(0, 3)}</span>
                                </label>
                              ))}
                            </div>
                          </div>
                          <div className="flex items-center justify-between">
                            <label className="flex items-center space-x-2">
                              <input
                                type="checkbox"
                                checked={scheduleConfig?.enabled}
                                onChange={(e) => handleScheduleUpdate({ enabled: e.target.checked })}
                                className="rounded"
                              />
                              <span className="text-xs sm:text-sm font-medium">Enable Scheduled Generation</span>
                            </label>
                          </div>
                          <Button 
                            onClick={generateNewsletter}
                            className="w-full bg-blue-600 text-white flex items-center justify-center gap-2 text-sm"
                            disabled={loading}
                          >
                            <Calendar className="w-4 h-4" />
                            Generate Now
                          </Button>
                          {scheduleConfig?.enabled && (
                            <p className="text-xs text-gray-500">
                              Next generation: {getNextGenerationTime()}
                            </p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>

                  <div className="lg:col-span-8">
                    <Card>
                      <CardContent className="p-4">
                        <h3 className="text-lg font-semibold mb-3">Generation History</h3>
                        <div className="space-y-2 max-h-[300px] lg:max-h-[400px] overflow-auto">
                          {newsletterStatus.map((status, index) => (
                            <div
                              key={status.date}
                              className="flex items-center justify-between p-2 bg-gray-50 rounded-md"
                            >
                              <div className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-gray-500" />
                                <div>
                                  <p className="text-sm font-medium">{new Date(status.date).toLocaleDateString('en-IN', {
                                    day: 'numeric',
                                    month: 'short',
                                    year: 'numeric',
                                    hour: '2-digit',
                                    minute: '2-digit',
                                    hour12: true,
                                    timeZone: 'Asia/Kolkata'
                                  })}</p>
                                  <p className="text-xs text-gray-500">
                                    Sent to {status.recipientCount} subscribers
                                  </p>
                                </div>
                              </div>
                              <div className="flex items-center gap-2">
                                {status.status === 'success' ? (
                                  <span className="text-green-600 flex items-center gap-1 text-xs">
                                    <CheckCircle className="w-3 h-3" /> Success
                                  </span>
                                ) : (
                                  <span className="text-red-600 flex items-center gap-1 text-xs">
                                    <AlertCircle className="w-3 h-3" /> Failed
                                  </span>
                                )}
                              </div>
                            </div>
                          ))}
                          {newsletterStatus.length === 0 && (
                            <p className="text-center text-gray-500 text-sm">No newsletter generation history available</p>
                          )}
                        </div>
                      </CardContent>
                    </Card>
                  </div>
                </div>
                <div className="mt-4 grid md:grid-cols-12 gap-4">
                  <div className="md:col-span-12">
                    <Card>
                      <CardContent className="p-4">
                        <div className="flex justify-between items-center mb-3">
                          <h3 className="text-lg font-semibold">Website Content Management</h3>
                          <Button
                            onClick={handleContentPublish}
                            className="bg-green-600 text-white flex items-center gap-2 text-sm px-3 py-2"
                            disabled={loading}
                          >
                            Publish Changes
                          </Button>
                        </div>
                        <div className="space-y-4">
                          {['aiTrends', 'testingTrends', 'industryTrends'].map((section) => (
                            <div key={section} className="border rounded-lg p-3">
                              <div className="flex justify-between items-center mb-2">
                                <h4 className="text-sm font-medium capitalize">
                                  {section.replace(/([A-Z])/g, ' $1').trim()}
                                </h4>
                                <Button
                                  onClick={() => setEditingContent(section)}
                                  className="bg-blue-600 text-white text-xs px-2 py-1"
                                  size="sm"
                                >
                                  Edit
                                </Button>
                              </div>
                              {editingContent === section ? (
                                <div className="space-y-2">
                                  <textarea
                                    className="w-full h-32 p-2 border rounded text-xs"
                                    value={JSON.stringify(websiteContent[section], null, 2)}
                                    onChange={(e) => {
                                      try {
                                        const parsed = JSON.parse(e.target.value);
                                        handleContentEdit(section, parsed);
                                      } catch (error) {
                                        // Allow invalid JSON during editing
                                      }
                                    }}
                                  />
                                  <div className="flex justify-end gap-2">
                                    <Button
                                      onClick={() => setEditingContent(null)}
                                      className="bg-gray-500 text-white text-xs px-2 py-1"
                                    >
                                      Cancel
                                    </Button>
                                    <Button
                                      onClick={() => {
                                        try {
                                          const content = JSON.parse(
                                            document.querySelector('textarea').value
                                          );
                                          handleContentEdit(section, content);
                                        } catch (error) {
                                          setError('Invalid JSON format');
                                        }
                                      }}
                                      className="bg-green-600 text-white text-xs px-2 py-1"
                                    >
                                      Save
                                    </Button>
                                  </div>
                                </div>
                              ) : (
                                <pre className="bg-gray-50 p-2 rounded overflow-auto max-h-24 text-xs">
                                  {JSON.stringify(websiteContent[section], null, 2)}
                                </pre>
                              )}
                            </div>
                          ))}
                        </div>
                        {websiteContent.lastUpdated && (
                          <p className="text-xs text-gray-500 mt-3">
                            Last updated: {new Date(websiteContent.lastUpdated).toLocaleString('en-IN', {
                              timeZone: 'Asia/Kolkata'
                            })}
                          </p>
                        )}
                      </CardContent>
                    </Card>
                  </div>
                </div>
              </TabsContent>

              <TabsContent value="contacts">
                <div className="space-y-4">
                  <Card>
                    <CardContent className="p-4">
                      <h3 className="text-lg font-semibold mb-3">Contact Messages</h3>
                      <div className="space-y-3 max-h-[600px] overflow-auto">
                        {contactMessages.map((message) => (
                          <div key={message.id} className="border rounded-lg p-3 space-y-2">
                            <div className="flex justify-between items-start">
                              <div>
                                <h4 className="font-semibold text-sm">{message.subject}</h4>
                                <p className="text-xs text-gray-500">
                                  From: {message.name} ({message.email})
                                  {message.company && ` - ${message.company}`}
                                </p>
                              </div>
                              <div className="flex items-center gap-2">
                                <select
                                  value={message.status}
                                  onChange={(e) => handleMessageStatus(message.id, e.target.value)}
                                  className="text-xs border rounded p-1"
                                >
                                  <option value="new">New</option>
                                  <option value="in-progress">In Progress</option>
                                  <option value="completed">Completed</option>
                                  <option value="archived">Archived</option>
                                </select>
                                <span className={`px-2 py-1 rounded text-xs ${
                                  message.status === 'new' ? 'bg-blue-100 text-blue-800' :
                                  message.status === 'in-progress' ? 'bg-yellow-100 text-yellow-800' :
                                  message.status === 'completed' ? 'bg-green-100 text-green-800' :
                                  'bg-gray-100 text-gray-800'
                                }`}>
                                  {message.status.charAt(0).toUpperCase() + message.status.slice(1)}
                                </span>
                              </div>
                            </div>
                            <p className="text-gray-700 bg-gray-50 p-2 rounded text-sm">{message.message}</p>
                            <div className="flex justify-between items-center text-xs text-gray-500">
                              <span>Received: {new Date(message.createdAt).toLocaleString('en-IN', {
                                timeZone: 'Asia/Kolkata'
                              })}</span>
                              {message.updatedAt && (
                                <span>Last Updated: {new Date(message.updatedAt).toLocaleString('en-IN', {
                                  timeZone: 'Asia/Kolkata'
                                })}</span>
                              )}
                            </div>
                          </div>
                        ))}
                        {contactMessages.length === 0 && (
                          <p className="text-center text-gray-500 text-sm">No contact messages received yet.</p>
                        )}
                      </div>
                    </CardContent>
                  </Card>
                </div>
              </TabsContent>

              <TabsContent value="tracking">
                <TrackingDashboard />
              </TabsContent>

              <TabsContent value="jobs">
                <AdminADAJobMonitoring />
              </TabsContent>

              <TabsContent value="analyst">
                <AnalystDailyReport />
              </TabsContent>


            </div>
          </Tabs>
        </div>
      </div>
    </div>
  );
};

export default AdminPanel;